import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Sparkles } from "lucide-react";

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function speak(text) {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "en-US";
  u.rate = 0.9;
  u.pitch = 1.1;
  window.speechSynthesis.speak(u);
}

function newRound() {
  const target = randomInt(1, 9);
  const options = new Set([target]);
  while (options.size < 4) options.add(randomInt(1, 9));
  return { target, options: Array.from(options).sort(() => Math.random() - 0.5) };
}

export default function CountingGame() {
  const [round, setRound] = useState(newRound());
  const [score, setScore] = useState(0);
  const [confetti, setConfetti] = useState([]);
  const [wrong, setWrong] = useState(null);

  const speakTarget = useCallback(() => {
    speak(`How many do you see?`);
  }, []);

  useEffect(() => { speakTarget(); }, [round, speakTarget]);

  const guess = (n) => {
    if (n === round.target) {
      setScore((s) => s + 1);
      const bits = Array.from({ length: 14 }).map((_, i) => ({
        id: `${Date.now()}-${i}`,
        tx: `${randomInt(-160, 160)}px`,
        ty: `${randomInt(-160, -20)}px`,
        color: ["#EF476F", "#FFD166", "#06D6A0", "#4CC9F0", "#B892FF"][randomInt(0, 4)],
        left: randomInt(30, 70),
      }));
      setConfetti(bits);
      speak(`Yes! ${round.target}. Well done!`);
      setTimeout(() => { setConfetti([]); setRound(newRound()); }, 1400);
    } else {
      setWrong(n);
      speak("Try again");
      setTimeout(() => setWrong(null), 500);
    }
  };

  return (
    <main className="mx-auto max-w-4xl px-6 py-10" data-testid="counting-game">
      <div className="flex items-center justify-between">
        <Link to="/games" className="inline-flex items-center gap-2 text-brand-ink/80 hover:text-brand-red font-bold">
          <ArrowLeft size={18} /> All games
        </Link>
        <div className="inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-1.5 font-bold shadow-brutal-sm" data-testid="counting-score">
          <Sparkles size={16} /> Score: {score}
        </div>
      </div>

      <h1 className="mt-4 font-display text-4xl md:text-5xl font-bold text-brand-ink">Counting Fun</h1>
      <p className="mt-2 text-brand-ink/80 text-lg">How many can you count?</p>

      <div className="mt-6 relative rounded-[36px] border-[3px] border-brand-ink bg-white p-8 shadow-brutal min-h-[280px]">
        <div className="flex flex-wrap justify-center items-center gap-3">
          {Array.from({ length: round.target }).map((_, i) => (
            <span
              key={i}
              className="text-5xl md:text-6xl animate-bounce-soft"
              style={{ animationDelay: `${i * 0.15}s` }}
              aria-hidden
              data-testid={`monkey-${i}`}
            >
              🐒
            </span>
          ))}
        </div>
        <button
          onClick={speakTarget}
          className="absolute top-4 right-4 rounded-full border-2 border-brand-ink bg-white px-3 py-1.5 text-sm font-bold hover:bg-brand-yellow"
          data-testid="counting-say"
        >
          🔊 Say it
        </button>
        {confetti.map((c) => (
          <span
            key={c.id}
            className="confetti-dot"
            style={{ "--tx": c.tx, "--ty": c.ty, background: c.color, left: `${c.left}%`, top: "50%" }}
          />
        ))}
      </div>

      <div className="mt-6 grid grid-cols-4 gap-3">
        {round.options.map((n) => (
          <button
            key={n}
            onClick={() => guess(n)}
            data-testid={`counting-option-${n}`}
            className={`rounded-3xl border-[3px] border-brand-ink bg-brand-red text-white font-display text-4xl font-bold py-6 shadow-brutal-sm lift-hover ${wrong === n ? "animate-wiggle bg-brand-ink/70" : ""}`}
          >
            {n}
          </button>
        ))}
      </div>

      <button
        onClick={() => setRound(newRound())}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-white border-2 border-brand-ink px-5 py-2 font-bold btn-brutal"
        data-testid="counting-new"
      >
        <RefreshCw size={16} /> New round
      </button>
    </main>
  );
}
